import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import i18next from 'i18next'
import last from 'lodash/last'

import { useFetchAllFormQuery, useFetchObjectByCodeNoCacheMutation } from '@redux/api'
import { useFetchContextMenuListQuery } from '@redux/api/contextMenu.api'
import { useFetchAllTooltipsQuery } from '@redux/api/tooltips.api'

import { usePrompt, useYupValidationResolver } from '@hooks'
import { getEntityAutocompleteOptionsFromType, getNewLastIndexByData } from '@helpers'
import { ACTION_CODE, ENTITY_TYPES_REQUEST, MODEL_TYPE } from '@constants'
import { AutocompleteOption, SelectOption } from '@types'

import {
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  PageContext,
} from '@gantt/components/GanttCreateOrEdit'
import {
  getCommandsFormattedRequest,
  getCommandsFormattedResponse,
  getDefaultCommandValueByActionCode,
  getIsDuplicateError,
  isSegmentTarget,
} from '@gantt/helpers'

import { ganttActionFormSchema } from '@validations'

import { getDefaultValue } from '../helpers'

import { useInputs } from './useInputs'

export const useAddGanttActionTimelinesDialog = () => {
  const {
    currentRow,
    onSetModalProperty,
    bindingValuesTimelineAction,
    bindingValuesActionTimelineEvents,
    onSetCurrentActionTimeline,
    currentSectionsList,
    currentBarObject,
    currentActionTimelineList,
    modalType,
  } = useContext(PageContext)

  const defaultValues = getDefaultValue(
    getNewLastIndexByData({
      items: currentActionTimelineList,
      getterNumber: value => last(value?.code?.split('_')),
    })
  )

  const resolver = useYupValidationResolver(ganttActionFormSchema)

  const methods = useForm<GanttActionType>({
    defaultValues,
    resolver,
  })
  const [isLoading] = useState(true)
  const [key, setKey] = useState(false)
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
    setError,
    clearErrors,
  } = methods

  const [fetchObjectByCode, { data: objectByCode }] = useFetchObjectByCodeNoCacheMutation({})

  const watchEventCode = watch('eventCode')
  const watchActionCode = watch('actionCode')
  const watchActionFieldBindType = watch('actionField.bindType')

  const objectCode = currentBarObject?.id

  const {
    data: forms,
    isFetching: isFetchingAllForm,
    isLoading: isLoadingAllForm,
  } = useFetchAllFormQuery(
    { objectCode },
    {
      skip: !currentBarObject?.id && watchActionCode !== ACTION_CODE.OPEN_FORM_MODEL,
    }
  )

  const {
    data: tooltips,
    isFetching: isFetchingTooltips,
    isLoading: isLoadingTooltips,
  } = useFetchAllTooltipsQuery(
    { objectCode },
    {
      skip: !currentBarObject?.id && watchActionCode !== ACTION_CODE.OPEN_TOOLTIP,
    }
  )

  const {
    data: contextMenus,
    isFetching: isFetchingContextMenus,
    isLoading: isLoadingContextMenus,
  } = useFetchContextMenuListQuery(
    { objectCode },
    {
      skip: !currentBarObject?.id && watchActionCode !== ACTION_CODE.OPEN_CONTEXT_MENU,
    }
  )

  const loading =
    isFetchingAllForm ||
    isLoadingAllForm ||
    isFetchingTooltips ||
    isLoadingTooltips ||
    isFetchingContextMenus ||
    isLoadingContextMenus

  usePrompt({ when: isDirty })

  useEffect(() => {
    currentBarObject?.id && fetchObjectByCode(currentBarObject?.id)
  }, [currentBarObject?.id])

  const actionValueOptions = useMemo(() => {
    let entity

    switch (watchActionCode) {
      case ACTION_CODE.OPEN_FORM_MODEL:
        entity = ENTITY_TYPES_REQUEST.FORM
        break
      case ACTION_CODE.OPEN_TOOLTIP:
        entity = ENTITY_TYPES_REQUEST.TOOLTIP
        break
      case ACTION_CODE.OPEN_CONTEXT_MENU:
        entity = ENTITY_TYPES_REQUEST.CONTEXT_MENU
        break
    }

    return getEntityAutocompleteOptionsFromType(entity, {
      [ENTITY_TYPES_REQUEST.FORM]: forms?.data,
      [ENTITY_TYPES_REQUEST.TOOLTIP]: tooltips,
      [ENTITY_TYPES_REQUEST.CONTEXT_MENU]: contextMenus,
    })
  }, [watchActionCode, forms?.data, tooltips, contextMenus])

  const actionTypeOptions: SelectOption[] = useMemo(
    () =>
      bindingValuesTimelineAction?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || [],
    [bindingValuesTimelineAction]
  )

  const eventOptions: SelectOption[] = useMemo(
    () =>
      bindingValuesActionTimelineEvents?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || [],
    [bindingValuesActionTimelineEvents]
  )

  const targetOptions: AutocompleteOption[] = useMemo(
    () =>
      currentSectionsList?.map(section => ({
        id: section.link.systemName,
        label: section.link.systemName,
      })) ?? [],
    [currentSectionsList]
  )

  const actionInputs = useInputs({
    actionTypeOptions,
    eventOptions,
    watchEventCode,
    targetOptions,
    methods,
    watchActionCode,
  })

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.GANTT_ACTION_TIMELINES)
  }, [])

  useEffect(() => {
    setKey(!key)
  }, [])

  useEffect(() => {
    if (currentRow && !loading && actionTypeOptions.length && eventOptions.length) {
      const currentActionRow = structuredClone(currentRow)
      const preparedActionField =
        currentActionRow.actionField.bindType === GANTT_BIND_TYPE_LIST.COMMANDS
          ? {
              ...currentActionRow.actionField,
              commands: getCommandsFormattedResponse(
                currentActionRow.actionField.commands,
                getDefaultCommandValueByActionCode(currentActionRow.actionCode)
              ),
            }
          : currentActionRow.actionField
      currentActionRow.actionField = preparedActionField
      reset(currentActionRow)

      setKey(!key)
    }
  }, [currentRow, loading, actionTypeOptions, eventOptions])

  const handleDuplicateError = useCallback((error: boolean) => {
    if (error) {
      setError('code', {
        type: 'custom',
        message: i18next.t('error.duplicatedSystemName'),
      })

      return
    }

    clearErrors('code')
  }, [])

  const handleSave = handleSubmit((data: GanttActionType) => {
    const { id = Date.now(), code, title, actionCode, eventCode, target, actionField } = data

    let newData: GanttActionType = {
      id,
      code,
      title,
      actionCode,
      eventCode,
      target: isSegmentTarget(eventCode) ? target : undefined,
      actionField,
    }

    if (actionField.bindType === GANTT_BIND_TYPE_LIST.COMMANDS) {
      const commands = getCommandsFormattedRequest(actionField.commands)
      const bindType =
        (actionCode === ACTION_CODE.EXECUTE_COMMAND && actionField.commands?.length) ||
        actionField.bindType === GANTT_BIND_TYPE_LIST.COMMANDS
          ? GANTT_BIND_TYPE_LIST.COMMANDS
          : null

      newData = {
        ...newData,
        actionField: {
          bindType,
          commands,
        },
      }
    }

    const isDuplicateError = getIsDuplicateError({
      itemsList: currentActionTimelineList,
      value: newData.code,
      modalType,
    })

    handleDuplicateError(isDuplicateError)

    !isDuplicateError && onSetCurrentActionTimeline(newData)
  })

  return {
    isDirty,
    actionInputs,
    methods,
    isLoading,
    handleSave,
    key,
    loading,
    watchActionCode,
    actionValueOptions,
    watchActionFieldBindType,
  }
}
